import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { OnboardingService } from '../../core/services/onboarding.service';
import { ToastService } from '../../core/services/toast.service';
import { ModalComponent, ModalErrorState } from '../../shared/components/modal/modal.component';
import { extractErrorDetails } from '../../core/interceptors/response.interceptor';
import {
  OnboardingRegisterRequest, OnboardingResponse, OnboardingStatus, MemberClass
} from '../../core/models/models';

import { PhoneInputComponent, isValidPhoneNumber } from '../../shared/components/phone-input/phone-input.component';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, ModalComponent, PhoneInputComponent],
  template: `
    <div
      class="min-h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden font-sans bg-cover bg-center"
      [style.backgroundImage]="'url(/img/zanzibar.jpg)'"
    >
      <!-- Pristine Backdrop Overlay -->
      <div class="absolute inset-0 bg-slate-900/40 backdrop-blur-[4px] z-0"></div>

      <!-- Floating Canvas particles inside login background -->
      <canvas id="loginParticles" class="absolute inset-0 z-1 pointer-events-none opacity-20"></canvas>

      <div class="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <!-- Official Government Seal & Brand Logos -->
        <div class="flex flex-col items-center gap-3">
          <div class="flex items-center gap-3 bg-white/90 px-5 py-2.5 rounded-full border border-slate-200 shadow-md backdrop-blur">
            <img src="/img/smz.png" class="w-10 h-auto" alt="SMZ Government Seal" />
            <div class="h-6 w-px bg-slate-200"></div>
            <div class="w-7 h-7 rounded-lg bg-emerald-600 flex items-center justify-center font-black text-white text-xs">ZX</div>
          </div>
        </div>

        <h2 class="mt-6 text-center text-2xl font-black text-white tracking-tight drop-shadow-md">
          {{ isRegistering() ? 'Institution Onboarding Portal' : 'ZAN-X CHANGE Portal' }}
        </h2>
        <p class="mt-2 text-center text-xs text-emerald-100 font-extrabold tracking-wider uppercase drop-shadow-sm">
          {{ isRegistering() ? 'Register your organization on Zan X-Change' : 'Consent & Data Sharing Management System' }}
        </p>
      </div>

      <div class="mt-8 sm:mx-auto sm:w-full relative z-10" [class.sm:max-w-md]="!isRegistering()" [class.sm:max-w-3xl]="isRegistering()">
        <div class="bg-white/95 p-8 rounded-3xl shadow-2xl border border-slate-200 backdrop-blur-md">
          
          <!-- Idle timeout message banner -->
          @if (idleTimeoutMessage()) {
            <div class="mb-5 p-4 rounded-2xl bg-amber-50 border border-amber-300 text-amber-950 text-xs flex items-start gap-3 shadow-sm animate-in fade-in duration-200">
              <svg class="w-5 h-5 shrink-0 text-amber-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <strong class="font-bold">Session Timed Out</strong>
                <p class="mt-0.5 text-amber-800">{{ idleTimeoutMessage() }}</p>
              </div>
            </div>
          }

          <!-- STATE: TWO-FACTOR (MFA) VERIFICATION -->
          @if (isMfaStep()) {
            <div class="space-y-6 animate-in fade-in duration-300">
              <div class="text-center">
                <div class="w-16 h-16 mx-auto mb-3 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center ring-8 ring-emerald-50">
                  <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 class="text-xl font-black text-slate-900 tracking-tight">Two-Factor Authentication</h3>
                <p class="text-xs text-slate-500 mt-1.5 leading-relaxed">
                  A 6-digit security code has been dispatched to:
                  <br/><span class="font-bold text-slate-900 font-mono text-sm">{{ maskedEmail() }}</span>
                </p>
              </div>

              @if (mfaError()) {
                <div class="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2">
                  <svg class="w-4 h-4 shrink-0 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{{ mfaError() }}</span>
                </div>
              }

              <div>
                <label class="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2 text-center">
                  Verification Code (OTP)
                </label>
                <div class="flex justify-center">
                  <input
                    type="text"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    maxlength="6"
                    [(ngModel)]="mfaCode"
                    (keyup.enter)="onVerifyMfa()"
                    placeholder="000000"
                    autofocus
                    class="w-64 text-center tracking-[0.4em] text-2xl font-mono font-black py-3 rounded-2xl border-2 border-slate-300 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 bg-slate-50 text-slate-900 placeholder-slate-300 transition"
                  />
                </div>
              </div>

              <button
                type="button"
                (click)="onVerifyMfa()"
                [disabled]="mfaLoading() || mfaCode.length !== 6"
                class="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-sm shadow-md shadow-emerald-600/20 transition-all transform active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                @if (mfaLoading()) {
                  <svg class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verifying...
                } @else {
                  Verify & Enter Portal
                }
              </button>

              <div class="flex items-center justify-between pt-3 border-t border-slate-200 text-xs">
                <button
                  type="button"
                  (click)="onCancelMfa()"
                  class="text-slate-500 hover:text-slate-800 font-bold transition flex items-center gap-1">
                  ← Back to Login
                </button>

                @if (resendCooldown() > 0) {
                  <span class="text-slate-400 font-mono">
                    Resend in {{ resendCooldown() }}s
                  </span>
                } @else {
                  <button
                    type="button"
                    (click)="onResendMfa()"
                    [disabled]="resendLoading()"
                    class="text-emerald-700 hover:text-emerald-800 font-bold transition flex items-center gap-1">
                    @if (resendLoading()) { Sending... } @else { Resend Code }
                  </button>
                }
              </div>
            </div>
          } @else if (!isRegistering()) {
            <form (ngSubmit)="onLogin()" class="space-y-6">
              <!-- Inline Form Error / Notice Banner -->
              @if (loginError(); as err) {
                @if (isInstitutionPendingApproval(err)) {
                  <!-- SUCCESS-STYLED NOTICE FOR INSTITUTION PENDING APPROVAL/ACTIVATION -->
                  <div class="p-4 rounded-2xl bg-emerald-50 border-2 border-emerald-300 text-emerald-950 text-xs space-y-2 shadow-md shadow-emerald-600/10 animate-in fade-in duration-200">
                    <div class="flex items-start justify-between font-bold gap-3">
                      <div class="flex items-start gap-2.5">
                        <div class="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 mt-0.5 border border-emerald-300">
                          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div class="space-y-1">
                          <div class="text-xs font-black text-emerald-950 uppercase tracking-wide">
                            Institution Registration Pending Approval
                          </div>
                          <p class="text-xs font-medium text-emerald-800 leading-relaxed">{{ err.message }}</p>
                        </div>
                      </div>
                      @if (err.code) {
                        <span class="font-mono text-[10px] px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 border border-emerald-300 font-extrabold shrink-0">
                          {{ err.code }}
                        </span>
                      }
                    </div>

                    @if (err.details && err.details.length > 0) {
                      <ul class="mt-2 text-xs space-y-1 bg-white/90 border border-emerald-200 rounded-xl p-2.5 font-mono text-emerald-800">
                        @for (detail of err.details; track detail) {
                          <li class="flex items-start gap-1.5">
                            <span class="text-emerald-500 font-bold select-none">•</span>
                            <span class="break-all">{{ detail }}</span>
                          </li>
                        }
                      </ul>
                    }
                  </div>
                } @else {
                  <div class="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-900 text-xs space-y-2 shadow-2xs">
                    <div class="flex items-center justify-between font-bold gap-2">
                      <div class="flex items-center gap-2">
                        <svg class="w-4 h-4 text-rose-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{{ err.message }}</span>
                      </div>
                      @if (err.code) {
                        <span class="font-mono text-[10px] px-2 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-300 font-extrabold shrink-0">
                          {{ err.code }}
                        </span>
                      }
                    </div>

                    @if (err.details && err.details.length > 0) {
                      <ul class="mt-2 text-xs space-y-1 bg-white/80 border border-rose-200 rounded-xl p-2.5 font-mono text-rose-800">
                        @for (detail of err.details; track detail) {
                          <li class="flex items-start gap-1.5">
                            <span class="text-rose-500 font-bold select-none">•</span>
                            <span class="break-all">{{ detail }}</span>
                          </li>
                        }
                      </ul>
                    }
                  </div>
                }
              }
              <div>
                <label for="email" class="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                  Official Email Address
                </label>
                <div class="relative">
                  <input
                    id="email"
                    type="email"
                    [(ngModel)]="email"
                    name="email"
                    required
                    placeholder="e.g. test@egaz.znz"
                    class="w-full px-4 py-3 rounded-xl border border-slate-300 bg-white text-slate-900 placeholder-slate-400 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                  />
                </div>
              </div>

              <div>
                <div class="flex items-center justify-between mb-2">
                  <label for="password" class="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Password
                  </label>
                  <a
                    routerLink="/forgot-password"
                    class="text-xs text-emerald-700 hover:text-emerald-800 font-bold transition-colors cursor-pointer"
                  >
                    Forgot Password?
                  </a>
                </div>
                <input
                  id="password"
                  type="password"
                  [(ngModel)]="password"
                  name="password"
                  required
                  placeholder="••••••••••••"
                  class="w-full px-4 py-3 rounded-xl border border-slate-300 bg-white text-slate-900 placeholder-slate-400 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                />
              </div>

              <button
                type="submit"
                [disabled]="loading()"
                class="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-sm shadow-md shadow-emerald-600/20 transition-all transform active:scale-95 disabled:opacity-50"
              >
                @if (loading()) {
                  <span class="inline-flex items-center gap-2">
                    <svg class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Authenticating...
                  </span>
                } @else {
                  Sign In to Portal
                }
              </button>

              <!-- Institutional Onboarding & Status Tracking Options -->
              <div class="pt-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-600">
                <div>
                  New Institution?
                  <a
                    routerLink="/onboarding"
                    class="font-bold text-emerald-700 hover:text-emerald-800 hover:underline pl-1 cursor-pointer"
                  >
                    Register Organization
                  </a>
                </div>
                <a
                  routerLink="/onboarding"
                  [queryParams]="{ view: 'lookup' }"
                  class="font-bold text-slate-700 hover:text-emerald-700 hover:underline inline-flex items-center gap-1.5 cursor-pointer bg-slate-50 hover:bg-emerald-50 px-3 py-1.5 rounded-xl border border-slate-200 transition shadow-2xs"
                >
                  <svg class="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span>Track Application Status</span>
                </a>
              </div>
            </form>
          }

          <!-- STATE B: PUBLIC INSTITUTION REGISTRATION WIZARD -->
          @if (isRegistering()) {
            <div class="space-y-6">
              <!-- Step Indicators -->
              <div class="flex items-center justify-between border-b border-slate-200 pb-6">
                @for (step of [1, 2, 3, 4]; track step) {
                  <div class="flex items-center gap-3">
                    <div
                      class="w-10 h-10 rounded-xl font-bold text-sm flex items-center justify-center transition-all shadow-2xs"
                      [ngClass]="currentStep() >= step ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/20' : 'bg-slate-100 text-slate-400'"
                    >
                      {{ step }}
                    </div>
                    <div class="hidden sm:block">
                      <div class="text-xs font-bold text-slate-900">Step {{ step }}</div>
                      <div class="text-[10px] text-slate-500 font-medium">
                        @if (step === 1) { Organization Info }
                        @if (step === 2) { Contact Person }
                        @if (step === 3) { Certificate & Letter }
                        @if (step === 4) { Token Summary }
                      </div>
                    </div>
                  </div>
                }
              </div>

              <!-- Step 1 Form -->
              @if (currentStep() === 1) {
                <div class="space-y-6">
                  <h3 class="text-base font-bold text-slate-900 border-l-4 border-emerald-600 pl-3">Step 1: Organization Profile & Classification</h3>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Official Institution Name</label>
                      <input
                        type="text"
                        [(ngModel)]="registerForm.institutionName"
                        placeholder="e.g. Zanzibar Maritime Authority"
                        class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs"
                      />
                    </div>

                    <div>
                      <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Government Vote Number</label>
                      <input
                        type="text"
                        [(ngModel)]="registerForm.voteNumber"
                        placeholder="e.g. V-409"
                        class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs font-mono"
                      />
                    </div>
                  </div>

                  <div>
                    <label class="block text-xs font-bold text-slate-700 uppercase mb-2">Member Sector Class</label>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      @for (mc of memberClasses; track mc.code) {
                        <div
                          (click)="registerForm.memberClassCode = mc.code"
                          class="p-4 rounded-xl border cursor-pointer transition-all flex flex-col justify-between"
                          [ngClass]="registerForm.memberClassCode === mc.code ? 'bg-emerald-50 border-emerald-400 text-emerald-900 shadow-2xs' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'"
                        >
                          <div class="font-bold text-xs flex items-center justify-between">
                            {{ mc.name }}
                            <span class="font-mono text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">{{ mc.code }}</span>
                          </div>
                          <p class="text-[11px] text-slate-500 mt-2">{{ mc.description }}</p>
                        </div>
                      }
                    </div>
                  </div>

                  <div class="flex justify-between pt-4 border-t border-slate-200">
                    <button
                      type="button"
                      (click)="cancelRegistration()"
                      class="px-5 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-semibold text-xs border border-slate-300 hover:bg-slate-200"
                    >
                      Back to Login
                    </button>
                    <button
                      (click)="goToRegisterStep(2)"
                      class="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-sm"
                    >
                      Proceed to Step 2 →
                    </button>
                  </div>
                </div>
              }

              <!-- Step 2 Form -->
              @if (currentStep() === 2) {
                <div class="space-y-6">
                  <h3 class="text-base font-bold text-slate-900 border-l-4 border-emerald-600 pl-3">Step 2: Contacts & Applicant</h3>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Institution Email</label>
                      <input
                        type="email"
                        [(ngModel)]="registerForm.institutionEmail"
                        placeholder="contact@agency.go.tz"
                        class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs"
                      />
                    </div>

                    <div>
                      <label class="block text-xs font-bold text-slate-700 uppercase mb-1">
                        Institution Phone <span class="text-rose-500">*</span>
                      </label>
                      <app-phone-input
                        [(ngModel)]="registerForm.institutionPhone"
                        (ngModelChange)="regInstPhoneError.set('')"
                        name="institutionPhone"
                        [required]="true"
                        [hasError]="!!regInstPhoneError()"
                        [errorMessage]="regInstPhoneError()"
                        placeholder="777 000 000"
                      ></app-phone-input>
                    </div>
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label class="block text-xs font-bold text-slate-700 uppercase mb-1">First Name *</label>
                      <input
                        type="text"
                        [(ngModel)]="registerForm.firstName"
                        placeholder="e.g. Omar"
                        class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Middle Name</label>
                      <input
                        type="text"
                        [(ngModel)]="registerForm.middleName"
                        placeholder="e.g. Said"
                        class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Last Name *</label>
                      <input
                        type="text"
                        [(ngModel)]="registerForm.lastName"
                        placeholder="e.g. Hassan"
                        class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-xs font-bold text-slate-700 uppercase mb-1">
                        Applicant Phone Number <span class="text-rose-500">*</span>
                      </label>
                      <app-phone-input
                        [(ngModel)]="registerForm.phone"
                        (ngModelChange)="regRepPhoneError.set('')"
                        name="repPhone"
                        [required]="true"
                        [hasError]="!!regRepPhoneError()"
                        [errorMessage]="regRepPhoneError()"
                        placeholder="777 000 000"
                      ></app-phone-input>
                    </div>

                    <div>
                      <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Applicant Direct Email</label>
                      <input
                        type="email"
                        [(ngModel)]="registerForm.applicantEmail"
                        placeholder="omar.hassan@ports.go.tz"
                        class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div class="flex justify-between pt-4 border-t border-slate-200">
                    <button
                      (click)="currentStep.set(1)"
                      class="px-5 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-semibold text-xs border border-slate-300 hover:bg-slate-200"
                    >
                      ← Back
                    </button>
                    <button
                      (click)="goToRegisterStep(3)"
                      class="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-sm"
                    >
                      Proceed to Step 3 →
                    </button>
                  </div>
                </div>
              }

              <!-- Step 3 Form -->
              @if (currentStep() === 3) {
                <div class="space-y-6">
                  <h3 class="text-base font-bold text-slate-900 border-l-4 border-emerald-600 pl-3">Step 3: Verification & Application Letter</h3>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Gazette Registration Number</label>
                      <input
                        type="text"
                        [(ngModel)]="registerForm.registrationNumber"
                        placeholder="e.g. ZMA/GAZ/2026/044"
                        class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs font-mono"
                      />
                    </div>

                    <div>
                      <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Compliance Certificate Number</label>
                      <input
                        type="text"
                        [(ngModel)]="registerForm.certificateNumber"
                        placeholder="e.g. CERT-ZMA-9941"
                        class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs font-mono"
                      />
                    </div>
                  </div>

                  <!-- PDF Browse & Upload Field -->
                  <div class="space-y-2">
                    <label class="block text-xs font-bold text-slate-700 uppercase tracking-wide">
                      Official Application Letter Attachment (PDF only, max 2MB)
                    </label>
                    <div class="flex items-center gap-3 p-4 rounded-xl bg-slate-50 border border-slate-300 hover:border-emerald-500 transition-colors">
                      <input
                        type="file"
                        accept="application/pdf"
                        (change)="onFileSelected($event)"
                        class="hidden"
                        id="appLetterFile"
                      />
                      <label for="appLetterFile" class="cursor-pointer bg-white hover:bg-slate-100 text-emerald-800 text-xs px-4 py-2 rounded-lg font-bold border border-slate-300 transition-all shadow-2xs">
                        Browse PDF
                      </label>
                      <span class="text-xs text-slate-600 font-mono truncate">
                        {{ fileName() || 'No PDF file selected' }}
                      </span>
                    </div>
                    @if (fileError()) {
                      <p class="text-rose-600 text-[11px] font-bold tracking-wide animate-pulse">
                        {{ fileError() }}
                      </p>
                    }
                  </div>

                  <div class="flex justify-between pt-4 border-t border-slate-200">
                    <button
                      (click)="currentStep.set(2)"
                      class="px-5 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-semibold text-xs border border-slate-300 hover:bg-slate-200"
                    >
                      ← Back
                    </button>
                    <button
                      (click)="onSubmitRegistration()"
                      [disabled]="!registerForm.registrationNumber || !registerForm.certificateNumber || !selectedFile() || !!fileError()"
                      class="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold text-xs shadow-md shadow-emerald-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Submit Registration Form
                    </button>
                  </div>
                </div>
              }

              <!-- Step 4 Success Summary -->
              @if (currentStep() === 4 && submittedOnboarding(); as result) {
                <div class="space-y-6 text-center py-4">
                  <div class="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto ring-4 ring-emerald-200">
                    <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>

                  <div class="space-y-2 max-w-lg mx-auto">
                    <h3 class="text-xl font-extrabold text-slate-900">Institution Onboarding Dispatched!</h3>
                    <p class="text-xs text-slate-600">
                      Your application has been registered into the eGAZ verification queue. Use the generated security token below to track onboarding progress.
                    </p>
                  </div>

                  <div class="p-6 rounded-2xl bg-slate-50 border border-emerald-300 max-w-md mx-auto space-y-2 shadow-2xs">
                    <span class="text-[10px] font-bold text-emerald-800 uppercase tracking-widest block">Onboarding Security Verification Token</span>
                    <div class="text-lg font-mono font-black text-slate-900 bg-white py-2.5 rounded-xl border border-slate-300 tracking-wider shadow-2xs">
                      {{ result.token }}
                    </div>
                    <div class="text-[11px] text-slate-500 font-medium">Token valid through {{ result.expirationDate }}</div>
                  </div>

                  <div class="pt-4">
                    <button
                      (click)="cancelRegistration()"
                      class="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-sm"
                    >
                      Return to Portal Login
                    </button>
                  </div>
                </div>
              }
            </div>
          }
        </div>
      </div>

      <!-- Forgot Password Modal -->
      <app-modal [isOpen]="showForgotPasswordModal()" title="Reset Access Password" [errorState]="modalError()" (close)="showForgotPasswordModal.set(false)">
        <div class="space-y-4">
          <p class="text-sm text-slate-600">
            Enter your registered email address to generate an OTP code for password reset.
          </p>
          <div>
            <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Email Address</label>
            <input
              type="email"
              [(ngModel)]="resetEmail"
              placeholder="user@institution.go.tz"
              class="w-full px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-sm focus:border-emerald-500"
            />
          </div>

          @if (otpSent()) {
            <div class="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs space-y-2">
              <div class="font-bold">OTP Code Dispatched!</div>
              <input
                type="text"
                [(ngModel)]="otpCode"
                placeholder="Enter 6-digit OTP"
                class="w-full px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-900 font-mono text-center tracking-widest text-base"
              />
            </div>
          }

          <div class="flex justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              (click)="showForgotPasswordModal.set(false)"
              class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300"
            >
              Cancel
            </button>
            @if (!otpSent()) {
              <button
                (click)="onSendOtp()"
                class="px-4 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 shadow-sm"
              >
                Send OTP Code
              </button>
            } @else {
              <button
                (click)="onVerifyOtp()"
                class="px-4 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 shadow-sm"
              >
                Verify & Reset
              </button>
            }
          </div>
        </div>
      </app-modal>
    </div>
  `
})
export class LoginComponent implements OnInit {
  authService = inject(AuthService);
  onboardingService = inject(OnboardingService);
  toastService = inject(ToastService);
  router = inject(Router);
  route = inject(ActivatedRoute);

  // Login variables
  email = '';
  password = '';
  loading = signal<boolean>(false);

  // Forgot password variables
  showForgotPasswordModal = signal<boolean>(false);
  resetEmail = '';
  otpSent = signal<boolean>(false);
  otpCode = '';

  // Public Onboarding registration wizard variables
  isRegistering = signal<boolean>(false);
  currentStep = signal<number>(1);
  memberClasses: MemberClass[] = [];

  loginError = signal<ModalErrorState | null>(null);
  modalError = signal<ModalErrorState | null>(null);

  // Security Hardening: Multi-Factor Authentication (MFA)
  isMfaStep = signal<boolean>(false);
  preAuthToken = signal<string>('');
  maskedEmail = signal<string>('');
  mfaCode = '';
  mfaLoading = signal<boolean>(false);
  mfaError = signal<string | null>(null);
  resendCooldown = signal<number>(0);
  resendLoading = signal<boolean>(false);
  private resendTimer: any = null;

  // Security Hardening: Inactivity Timeout Notification
  idleTimeoutMessage = signal<string | null>(null);

  // File Upload variables
  selectedFile = signal<File | null>(null);
  fileName = signal<string>('');
  fileError = signal<string>('');

  registerForm: OnboardingRegisterRequest = {
    institutionName: '',
    voteNumber: '',
    registrationNumber: '',
    memberClassCode: 'GOV',
    institutionEmail: '',
    institutionPhone: '',
    certificateNumber: '',
    firstName: '',
    middleName: '',
    lastName: '',
    phone: '',
    applicantEmail: ''
  };

  regInstPhoneError = signal<string>('');
  regRepPhoneError = signal<string>('');

  submittedOnboarding = signal<OnboardingResponse | null>(null);

  constructor() {
    this.memberClasses = this.onboardingService.memberClasses;
  }

  ngOnInit() {
    this.loadMemberClasses();

    // Listen for query parameters from landing page or idle timeout logout
    this.route.queryParams.subscribe(params => {
      if (params['register'] === 'true') {
        this.isRegistering.set(true);
      }
      if (params['reason'] === 'idle_timeout') {
        this.idleTimeoutMessage.set('Your session expired after 5 minutes of inactivity for security reasons. Please log in again.');
      }
    });

    // Initialize floating network connection canvas in login page background
    this.initBackgroundParticles();
  }

  loadMemberClasses() {
    this.onboardingService.getMemberClasses().subscribe({
      next: (res) => {
        if (res.success && res.data && res.data.length > 0) {
          this.memberClasses = res.data;
          if (!this.registerForm.memberClassCode && this.memberClasses.length > 0) {
            this.registerForm.memberClassCode = this.memberClasses[0].code;
          }
        }
      },
      error: () => {
        this.memberClasses = this.onboardingService.memberClasses;
      }
    });
  }

  onLogin() {
    this.loginError.set(null);
    this.loading.set(true);
    this.authService.login({ email: this.email, password: this.password }).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.data?.mfaRequired) {
          // Enter MFA verification step
          this.isMfaStep.set(true);
          this.preAuthToken.set(res.data.preAuthToken || '');
          this.maskedEmail.set(res.data.maskedEmail || this.email);
          this.mfaCode = '';
          this.mfaError.set(null);
          this.startResendCooldown();
          this.toastService.info('Verification code sent to ' + (res.data.maskedEmail || this.email));
        } else {
          this.toastService.success(res.message || 'Authenticated successfully');
          this.router.navigate(['/dashboard']);
        }
      },
      error: (err) => {
        this.loading.set(false);
        const errDetails = extractErrorDetails(err.error || err);
        this.loginError.set(errDetails);
      }
    });
  }

  onVerifyMfa() {
    if (!this.mfaCode || this.mfaCode.trim().length !== 6) {
      this.mfaError.set('Please enter the 6-digit verification code.');
      return;
    }

    this.mfaError.set(null);
    this.mfaLoading.set(true);

    this.authService.verifyMfa({
      preAuthToken: this.preAuthToken(),
      otp: this.mfaCode.trim()
    }).subscribe({
      next: (res) => {
        this.mfaLoading.set(false);
        this.toastService.success(res.message || 'Authenticated successfully');
        this.router.navigate(['/dashboard']);
      },
      error: (err) => {
        this.mfaLoading.set(false);
        const errDetails = extractErrorDetails(err.error || err);
        this.mfaError.set(errDetails.message || 'Invalid or expired verification code.');
      }
    });
  }

  onResendMfa() {
    if (this.resendCooldown() > 0 || this.resendLoading()) return;

    this.resendLoading.set(true);
    this.mfaError.set(null);

    this.authService.resendMfa({
      preAuthToken: this.preAuthToken()
    }).subscribe({
      next: (res) => {
        this.resendLoading.set(false);
        this.startResendCooldown();
        this.toastService.success(res.message || 'Verification code resent successfully');
      },
      error: (err) => {
        this.resendLoading.set(false);
        const errDetails = extractErrorDetails(err.error || err);
        this.mfaError.set(errDetails.message || 'Failed to resend verification code');
      }
    });
  }

  startResendCooldown() {
    this.resendCooldown.set(60);
    if (this.resendTimer) clearInterval(this.resendTimer);
    this.resendTimer = setInterval(() => {
      const current = this.resendCooldown();
      if (current > 1) {
        this.resendCooldown.set(current - 1);
      } else {
        this.resendCooldown.set(0);
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
    }, 1000);
  }

  onCancelMfa() {
    this.isMfaStep.set(false);
    this.mfaCode = '';
    this.mfaError.set(null);
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
  }

  isInstitutionPendingApproval(err: ModalErrorState | null): boolean {
    if (!err) return false;
    if (err.code === 'ZANX-1011') return true;
    const msg = (err.message || '').toLowerCase();
    return msg.includes('institution') && (
      msg.includes('approv') ||
      msg.includes('activat') ||
      msg.includes('pending') ||
      msg.includes('not yet')
    );
  }

  onSendOtp() {
    this.authService.forgotPassword({ email: this.resetEmail }).subscribe({
      next: () => {
        this.otpSent.set(true);
        this.toastService.success('OTP sent to email address');
      },
      error: () => {
        this.toastService.error('Failed to send OTP');
      }
    });
  }

  onVerifyOtp() {
    if (!this.otpCode) {
      this.toastService.error('Please enter the OTP code');
      return;
    }
    this.authService.validateOtp({ email: this.resetEmail, otp: this.otpCode }).subscribe({
      next: (res) => {
        if (res.success) {
          this.toastService.success('OTP verified successfully! Password reset unlocked.');
          this.showForgotPasswordModal.set(false);
          this.otpSent.set(false);
        } else {
          this.toastService.error(res.message || 'Invalid OTP code');
        }
      },
      error: () => {
        this.toastService.error('Invalid OTP code');
      }
    });
  }

  // File picker handler with type and size verification
  onFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    // Validate size (must be < 10MB: 10 * 1024 * 1024 bytes)
    const maxSizeInBytes = 10 * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      this.selectedFile.set(null);
      this.fileName.set('');
      this.fileError.set('Error: File size must be less than 10MB.');
      this.toastService.error('File size must be less than 10MB');
      return;
    }

    const fileExt = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : 'doc';

    this.selectedFile.set(file);
    this.fileName.set(file.name);
    this.fileError.set('');

    const reader = new FileReader();
    reader.onload = () => {
      const resultString = reader.result as string;
      const base64Content = resultString.includes(',') ? resultString.split(',')[1] : resultString;
      this.registerForm.applicationLetter = {
        content: base64Content,
        fileType: fileExt || 'doc'
      };
      this.toastService.success(`Loaded application letter: ${file.name} (fileType: ${fileExt})`);
    };
    reader.readAsDataURL(file);
  }

  validateRegisterStep2(): boolean {
    let valid = true;
    if (!this.registerForm.institutionPhone?.trim()) {
      this.regInstPhoneError.set('Institution phone number is required.');
      valid = false;
    } else if (!isValidPhoneNumber(this.registerForm.institutionPhone)) {
      this.regInstPhoneError.set('Institution phone must be 9 digits after country code (e.g. +255 777 000 000).');
      valid = false;
    } else {
      this.regInstPhoneError.set('');
    }

    if (!this.registerForm.phone?.trim()) {
      this.regRepPhoneError.set('Applicant phone number is required.');
      valid = false;
    } else if (!isValidPhoneNumber(this.registerForm.phone)) {
      this.regRepPhoneError.set('Applicant phone must be 9 digits after country code (e.g. +255 777 000 000).');
      valid = false;
    } else {
      this.regRepPhoneError.set('');
    }

    if (!this.registerForm.firstName?.trim() || !this.registerForm.lastName?.trim()) {
      this.toastService.warning('Please enter applicant first name and last name.');
      valid = false;
    }

    if (!valid && (this.regInstPhoneError() || this.regRepPhoneError())) {
      this.toastService.warning(this.regInstPhoneError() || this.regRepPhoneError());
    }

    return valid;
  }

  goToRegisterStep(step: number) {
    if (step === 2) {
      if (!this.registerForm.institutionName?.trim()) {
        this.toastService.warning('Please enter official institution name.');
        return;
      }
    }
    if (step === 3) {
      if (!this.validateRegisterStep2()) {
        return;
      }
    }
    this.currentStep.set(step);
  }

  // Registration wizard actions
  onSubmitRegistration() {
    if (!this.validateRegisterStep2()) {
      this.currentStep.set(2);
      return;
    }
    if (!this.registerForm.applicationLetter || !this.registerForm.applicationLetter.content) {
      if (this.selectedFile()) {
        const file = this.selectedFile()!;
        const fileExt = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : 'doc';
        const reader = new FileReader();
        reader.onload = () => {
          const resultString = reader.result as string;
          const base64Content = resultString.includes(',') ? resultString.split(',')[1] : resultString;
          this.registerForm.applicationLetter = {
            content: base64Content,
            fileType: fileExt || 'doc'
          };
          this.executeRegistrationSubmit();
        };
        reader.readAsDataURL(file);
        return;
      } else {
        this.toastService.error('Please upload your official application letter before submitting.');
        return;
      }
    }
    this.executeRegistrationSubmit();
  }

  private executeRegistrationSubmit() {
    this.onboardingService.registerInstitution(this.registerForm).subscribe(res => {
      if (res.success && res.data) {
        this.submittedOnboarding.set(res.data);
        this.currentStep.set(4);
        this.toastService.success(res.message);
      }
    });
  }

  cancelRegistration() {
    this.isRegistering.set(false);
    this.currentStep.set(1);
    this.submittedOnboarding.set(null);
    this.selectedFile.set(null);
    this.fileName.set('');
    this.fileError.set('');
    this.regInstPhoneError.set('');
    this.regRepPhoneError.set('');
  }

  initBackgroundParticles() {
    const canvas = document.getElementById('loginParticles') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = canvas.width = canvas.offsetWidth;
    let height = canvas.height = canvas.offsetHeight;

    window.addEventListener('resize', () => {
      if (canvas) {
        width = canvas.width = canvas.offsetWidth;
        height = canvas.height = canvas.offsetHeight;
      }
    });

    const particles: Array<{ x: number; y: number; vx: number; vy: number; radius: number }> = [];
    const particleCount = 20;

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        radius: Math.random() * 2 + 1
      });
    }

    const animate = () => {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, width, height);

      // Draw connection lines
      ctx.strokeStyle = 'rgba(20, 184, 166, 0.1)';
      ctx.lineWidth = 0.8;
      for (let i = 0; i < particleCount; i++) {
        for (let j = i + 1; j < particleCount; j++) {
          const dist = Math.hypot(particles[i].x - particles[j].x, particles[i].y - particles[j].y);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw glowing particle nodes
      ctx.fillStyle = 'rgba(20, 184, 166, 0.3)';
      for (let i = 0; i < particleCount; i++) {
        const p = particles[i];
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fill();

        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;
      }

      requestAnimationFrame(animate);
    };

    animate();
  }
}
